import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Select } from "@narmi/design_system";
import ApiHttp from "byzantine/src/ApiHttp";
import Organization from "byzantine/src/Organization";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Dialog, useLoadingContext, useNotificationContext } from "cerulean";
import { useCurrentUser } from "../contexts/CurrentUserContext";

export enum ZelleUrl {
  FIS = "zelle/fis/token",
  FISERV = "zelle/fiserv",

}

type AffiliatedOrg = {
  uuid: string;
  name_legal: string;
  name_dba: string | null;
};

const Zelle = ({ url }: {url: ZelleUrl}) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { currentUser } = useCurrentUser();

  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [organizations, setOrganizations] = useState<AffiliatedOrg[]>([]);
  const [selectedOrgUuid, setSelectedOrgUuid] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const getSso = useCallback(async (orgUuid?: string) => {
    if (!iframeRef?.current) return;

    const payload: Record<string, string> = {};
    if (orgUuid) {
      payload.organization_uuid = orgUuid;
    }

    let response = null;
    setIsLoading(true);
    try {
      response = await ApiHttp.fetch(url, { method: "POST" }, payload);
      if (!response?.preencoded_url) throw Error("no sso url returned");
      iframeRef.current.src = response.preencoded_url;
    } catch {
      if(url === ZelleUrl.FIS && response && response.status === 409 ) {
        sendNotification({
          type: "negative",
          text: response.message,
        });
      } else {
        sendNotification({
          type: "negative",
          text: l10n.getString("error-unknown"),
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [url, l10n, sendNotification, setIsLoading]);

  useEffect(() => {
    if (!currentUser?.org_uuid) {
      getSso();
      return;
    }
    Organization.getOrganization(currentUser.org_uuid).then((org) => {
      if (!org.affiliated_organizations?.length) {
        getSso();
        return;
      }
      const allOrgs: AffiliatedOrg[] = [
        { uuid: org.uuid, name_legal: org.name_legal, name_dba: org.name_dba },
        ...org.affiliated_organizations,
      ];
      setOrganizations(allOrgs);
      setSelectedOrgUuid(allOrgs[0].uuid);
      setIsDialogOpen(true);
    });
  }, []);

  const handleConfirm = () => {
    setIsDialogOpen(false);
    getSso(selectedOrgUuid);
  };

  return (
    <>
      <Dialog
        isOpen={isDialogOpen}
        title={l10n.getString("zelle-select-organization-title")}
        onUserDismiss={() => navigate("/transfer")}
        footer={
          <Button
            onClick={handleConfirm}
            disabled={!selectedOrgUuid}
          >
            {l10n.getString("zelle-select-organization-confirm")}
          </Button>
        }
      >
        <Select
          id="zelle-org-select"
          label={l10n.getString("zelle-select-organization-label")}
          value={selectedOrgUuid}
          onChange={(value: string) => setSelectedOrgUuid(value)}
        >
          {organizations.map((org) => (
            <Select.Item key={org.uuid} value={org.uuid}>
              {org.name_dba || org.name_legal}
            </Select.Item>
          ))}
        </Select>
      </Dialog>
      <iframe ref={iframeRef} className="zelle-iframe" />
    </>
  );
};

export default Zelle;
